import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { ChevronDown } from 'lucide-react';
import React from 'react';
import {
  color,
  control,
  dialog,
  font,
  radius,
  role,
  shadow,
  space,
  spaceNum,
} from '../../shared/ui';
import { TooltipProvider } from '../../shared/ui/primitives/tooltip';
import { controlOverrides } from './themeControls';
import { tableOverrides } from './themeTables';

/* ------------------------------------------------------------------ The field look
 *
 * Redesign iteration 1: one field everywhere. Outlined, 40px, a 1px --input-border edge
 * that darkens under the pointer and turns brand with a 3px halo on focus, and the label
 * always static above the box, so a filled field and an empty one look the same.
 *
 * All of it is scoped to MUI's *outlined* variant. 34 call sites ask for `standard` and
 * they keep their underline and their floating label; the shared form components are
 * what moved to outlined, so that is where the new look arrives.
 */

/** A field's inset from its edge. */
const FIELD_PAD_X = spaceNum[12];
/* The vertical inset: MUI sets its own line box (1.4375em), so this is whatever pads that
   line out to the input height. An input, a select and a date field all come out at
   exactly --control-h-input, whatever the text size. */
const FIELD_PAD_Y = `calc((${control.inputHeight}px - 1.4375em) / 2)`;
/* A multi-select's chips sit 3px in from its edge (MUI's own tag margin is the other 3),
   so its row is the input height less both, and the text input is padded to fill it. */
const CHIP_INSET = spaceNum[3];
const CHIP_ROW_PAD_Y = `calc((${control.inputHeight - 2 * CHIP_INSET}px - 1.4375em) / 2)`;
/** The chevron's size, as a Lucide `size`. */
const CHEVRON = 16;
/** The halo a focused field wears. */
const halo = (ring) => `0 0 0 ${space[3]} ${ring}`;

/**
 * The select chevron. MUI hands its icon the select's variant as a class, so this draws
 * the design's chevron on an outlined select and MUI's own triangle on a standard or
 * filled one, which keeps every select that asked for the old look exactly as it was.
 */
export const FieldChevron = React.forwardRef(({ className = '', ...props }, ref) =>
  /-iconOutlined\b/.test(className) ? (
    <ChevronDown ref={ref} className={className} size={CHEVRON} aria-hidden="true" {...props} />
  ) : (
    <ArrowDropDownIcon ref={ref} className={className} {...props} />
  )
);
FieldChevron.displayName = 'FieldChevron';

/**
 * A FormControl whose own input is outlined. MUI takes a label's variant from the
 * FormControl rather than from the input beside it, and a bare `<FormControl>` defaults
 * to outlined, so a hand-written label over a standard Select (GenericSelect, and the
 * shared FieldWrapper's legacy path) calls itself outlined too. Asking the DOM what the
 * input actually is keeps those as they were; a browser without `:has()` simply keeps
 * MUI's floating label everywhere.
 */
const OUTLINED_CONTROL = '&:has(> .MuiOutlinedInput-root)';

/** The label above an outlined field: 13/600 --ink-2, static, 6px over the box. */
export const staticLabel = {
  position: 'relative',
  transform: 'none',
  maxWidth: '100%',
  whiteSpace: 'normal',
  overflow: 'visible',
  pointerEvents: 'auto',
  zIndex: 'auto',
  transition: 'none',
  marginBottom: control.labelGap,
  fontSize: font.size.small,
  fontWeight: font.weightWanted.semibold,
  color: color.ink2,
  // Focus, an error and a disabled field all say so in the box, not in the label.
  '&.Mui-focused, &.Mui-error, &.Mui-disabled': { color: color.ink2 },
  '& .MuiFormLabel-asterisk': { color: color.errorText },
};

/** Help text 6px under its box, and the error in the same slot. */
export const fieldHelperText = {
  marginTop: control.labelGap,
  marginLeft: 0,
  marginRight: 0,
  fontSize: font.size.caption,
  color: color.muted,
  '&.Mui-error': { color: color.errorText, fontWeight: font.weightWanted.semibold },
};

/** A selected value inside an outlined multi-select: 28 tall, on --fill, removable. */
const fieldChip = {
  height: control.chipHeight,
  margin: CHIP_INSET,
  borderRadius: radius.chip,
  backgroundColor: color.fill,
  color: color.ink2,
  fontSize: font.size.small,
  '& .MuiChip-label': { paddingLeft: spaceNum[10], paddingRight: spaceNum[4] },
  // The remove control is a 20px target around a 14px cross.
  '& .MuiChip-deleteIcon': {
    boxSizing: 'border-box',
    width: spaceNum[20],
    height: spaceNum[20],
    padding: spaceNum[3],
    margin: `0 ${spaceNum[4]}px 0 0`,
    borderRadius: radius.check,
    color: color.muted,
    '&:hover': { color: color.ink },
  },
  // Disabled never uses opacity: an opacity of a token is a new colour.
  '&.Mui-disabled': { opacity: 1, backgroundColor: color.line, color: color.muted },
};

const fieldOverrides = {
  MuiFormControl: {
    styleOverrides: {
      root: {
        [OUTLINED_CONTROL]: {
          '& > .MuiInputLabel-root': staticLabel,
          '& > .MuiFormHelperText-root': fieldHelperText,
          /* MUI hides the placeholder while a floating label sits where it would be. A
             static label never does, so the placeholder shows whenever the box is empty. */
          '& > label[data-shrink="false"] + .MuiOutlinedInput-root .MuiInputBase-input::placeholder':
            { opacity: '1 !important' },
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: radius.control,
        backgroundColor: color.white,
        color: color.ink,
        // A minimum, so a multi-line field and a multi-select that wraps its chips grow.
        minHeight: control.inputHeight,
        // Hover first, so focus, an error and a disabled field all win over it.
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: color.muted },
        '&.Mui-focused': { boxShadow: halo(color.ringBrand) },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: color.brand,
          borderWidth: 1,
        },
        '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: color.danger },
        '&.Mui-error.Mui-focused': { boxShadow: halo(color.ringDanger) },
        '&.Mui-disabled': {
          backgroundColor: color.fill,
          color: color.muted,
          cursor: 'not-allowed',
        },
        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: color.borderLight },
      },
      adornedStart: { paddingLeft: FIELD_PAD_X },
      adornedEnd: { paddingRight: FIELD_PAD_X },
      multiline: { padding: `${spaceNum[10]}px ${FIELD_PAD_X}px` },
      /* No notch: the label is above the box, so the edge runs unbroken. The legend is
         what cuts the notch and what the fieldset's -5px top makes room for. */
      notchedOutline: {
        top: 0,
        borderColor: role.inputBorder,
        '& legend': { display: 'none' },
      },
      input: {
        padding: `${FIELD_PAD_Y} ${FIELD_PAD_X}px`,
        '&::placeholder': { color: role.placeholderInk, opacity: 1 },
        '&.Mui-disabled': { WebkitTextFillColor: color.muted, cursor: 'not-allowed' },
      },
      inputMultiline: { padding: 0 },
      inputAdornedStart: { paddingLeft: 0 },
      inputAdornedEnd: { paddingRight: 0 },
    },
  },
  MuiSelect: {
    // Every select gets the chevron; see FieldChevron for why standard ones keep MUI's.
    defaultProps: { IconComponent: FieldChevron },
    styleOverrides: {
      outlined: {
        // Room for the chevron: its 12px inset, its 16px and 8px of air before the text.
        '&&&': { paddingRight: FIELD_PAD_X + CHEVRON + spaceNum[8] },
        // MUI greys a focused select; the box already shows focus with its edge and halo.
        '&:focus': { backgroundColor: 'transparent', borderRadius: radius.control },
      },
      iconOutlined: {
        right: FIELD_PAD_X,
        top: `calc(50% - ${CHEVRON / 2}px)`,
        color: color.muted,
        '&.Mui-disabled': { color: color.subtle },
      },
    },
  },
  MuiAutocomplete: {
    styleOverrides: {
      root: {
        '& .MuiAutocomplete-inputRoot.MuiOutlinedInput-root': {
          paddingTop: CHIP_INSET,
          paddingBottom: CHIP_INSET,
          paddingLeft: spaceNum[6],
        },
        // The text sits 12px in from the edge, where every other field's does.
        '& .MuiAutocomplete-inputRoot.MuiOutlinedInput-root .MuiAutocomplete-input': {
          padding: `${CHIP_ROW_PAD_Y} ${spaceNum[4]}px ${CHIP_ROW_PAD_Y} ${spaceNum[6]}px`,
        },
        '& .MuiOutlinedInput-root .MuiAutocomplete-tag': fieldChip,
      },
      // The option list sits on the menu surface, as the select menus do.
      paper: { boxShadow: shadow.lg },
      // The chevron in --muted, as on every select.
      popupIndicator: { color: color.muted },
    },
  },
  // The date and time pickers' popover: the menu surface, and the brand on the chosen day.
  MuiPickersPopper: {
    styleOverrides: { paper: { borderRadius: radius.card, boxShadow: shadow.lg } },
  },
  MuiPickersDay: {
    styleOverrides: {
      root: {
        '&.Mui-selected': { backgroundColor: color.brand, color: color.white },
        '&.MuiPickersDay-today:not(.Mui-selected)': { borderColor: color.border },
      },
    },
  },
};

/**
 * The MUI theme, derived entirely from `shared/ui/tokens`.
 *
 * **Why this file matters more than it looks.** There are 859 MUI elements across 136
 * screens, and none of them is going to be rewritten: `Box`, `Typography` and `Grid`
 * alone are 362 of them, and `Autocomplete`, `mui-datatables`, the data grid and the date
 * pickers are kept deliberately for their behaviour. Without a theme they render MUI's
 * defaults, which means a third of the CMS would ignore a palette change entirely. With
 * one, they follow it for free.
 *
 * So the token set now reaches the whole app through three consumers, and a redesign is
 * an edit to `css/tokens.css` plus its mirror in `shared/ui/tokens.js`:
 *
 *     css/tokens.css ──┬─→ tailwind.config.js  (var)  every Tailwind utility
 *                      ├─→ main.css            (var)  every legacy rule
 *                      └─→ shared/ui/tokens.js (hex)  this theme, and so all of MUI
 *
 * The CSS and JS halves are kept in step by `shared/ui/__tests__/tokensCss.test.js`,
 * which compares them colour by colour and dimension by dimension.
 *
 * **Why hex here and `var()` in Tailwind.** MUI computes on palette colours: `alpha()`,
 * hover and active shades, contrast text. Those need a real value, and `var(--brand)`
 * would throw or silently produce transparent. Tailwind has no such need, so it points at
 * the custom property and gets live updates. Same source, two forms, one test.
 *
 * `palette.bootstrap` is not MUI's; it predates this file and a handful of screens read
 * `theme.palette.bootstrap.blue`. It stays until those are converted.
 */
const theme = createTheme({
  palette: {
    primary: { main: color.brand, dark: color.brandHover, light: color.brandFocus },
    secondary: { main: color.chrome },
    error: { main: color.danger, dark: color.dangerStrong },
    warning: { main: color.warning },
    success: { main: color.successDot, dark: color.success },
    info: { main: color.accent, dark: color.accentDark, light: color.accentSurface },
    text: {
      primary: color.ink,
      secondary: color.muted,
      disabled: color.subtle,
    },
    background: { default: color.white, paper: color.white },
    divider: color.borderLight,
    action: { disabled: color.subtle, disabledBackground: color.fill },
    // Pre-existing and still read by a few screens. Not MUI's own shape.
    bootstrap: {
      blue: color.brand,
      green: color.success,
      red: color.danger,
      yellow: color.warning,
      grey: color.neutral600,
    },
  },

  shape: { borderRadius: radius.control },

  // 4px base, matching `space` in tokens.js and the spacing scale in tailwind.config.js.
  spacing: 4,

  typography: {
    fontFamily: font.family,
    fontSize: font.size.body,
    // Only the two weights that exist as files. See the note in tokens.js: asking for 600
    // resolves to the Bold face anyway, so naming it here would be a lie about the assets.
    fontWeightRegular: font.weight.regular,
    fontWeightMedium: font.weight.bold,
    fontWeightBold: font.weight.bold,
    h1: { fontSize: font.size.title, fontWeight: font.weight.bold },
    h2: { fontSize: font.size.section, fontWeight: font.weight.bold },
    h3: { fontSize: font.size.section, fontWeight: font.weight.bold },
    body1: { fontSize: font.size.body },
    body2: { fontSize: font.size.small },
    caption: { fontSize: font.size.caption },
    button: { fontSize: font.size.body, textTransform: 'none' },
  },

  shadows: (() => {
    // MUI insists on exactly 25 entries. Keep its ramp but replace the three the CMS
    // actually uses so a shadow change lands here rather than in each component.
    const base = createTheme().shadows.slice();
    base[1] = shadow.xs;
    base[8] = shadow.lg;
    base[24] = shadow.xl;
    return base;
  })(),

  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: radius.control,
          textTransform: 'none',
          paddingTop: control.padY,
          paddingBottom: control.padY,
          paddingLeft: spaceNum[16],
          paddingRight: spaceNum[16],
        },
      },
    },
    MuiInputBase: {
      styleOverrides: { root: { fontSize: font.size.body } },
    },
    // The outlined field, its label, its select, chips and pickers. See the top of the file.
    ...fieldOverrides,
    MuiPaper: {
      styleOverrides: { rounded: { borderRadius: radius.card } },
    },
    MuiDialog: {
      styleOverrides: {
        // MUI centres its panel by flexing the scroll container. Anchoring it to the top
        // and pushing it down by the same offset the Radix dialogs use is what keeps the
        // two families opening in the same place.
        container: { alignItems: 'flex-start' },
        paper: {
          borderRadius: radius.dialog,
          marginTop: dialog.top,
          maxHeight: dialog.maxHeight,
        },
      },
    },
    // The header, body and footer of the shared dialog (shared/ui/primitives/dialog.js):
    // 12/16 insets, the title in section type, and a hairline under the header and over
    // the footer. MUI's own were 16/24, 20/24 and 8, with a 20px title and no rules.
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: `${space[12]} ${space[16]}`,
          color: color.ink,
          fontSize: font.size.section,
          fontWeight: font.weight.bold,
          borderBottom: `1px solid ${color.borderLight}`,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: space[16],
          // MUI drops the top inset after a title, which sat flush under it without a rule.
          '.MuiDialogTitle-root + &': { paddingTop: space[16] },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: `${space[12]} ${space[16]}`,
          borderTop: `1px solid ${color.borderLight}`,
        },
      },
    },
    MuiMenu: {
      styleOverrides: { paper: { borderRadius: radius.card, boxShadow: shadow.lg } },
    },
    MuiMenuItem: {
      styleOverrides: { root: { fontSize: font.size.body } },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: color.ink,
          fontSize: font.size.caption,
          borderRadius: radius.control,
        },
      },
    },
    MuiChip: {
      styleOverrides: { root: { borderRadius: radius.chip } },
    },
    // The same box as the CMS's own messages (`.alert` in main.css, the growls, and the
    // notice): its radius, and one colour pair per tone, with the icon in the text colour.
    // MUI worked each tone out from the palette, so its boxes matched nothing else, and its
    // warning icon was the yellow of the pill dot on a pale yellow ground.
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 'var(--radius-track)', fontSize: font.size.body },
        standardSuccess: {
          color: 'var(--ok-ink)',
          backgroundColor: 'var(--ok-bg)',
          '& .MuiAlert-icon': { color: 'inherit' },
        },
        standardWarning: {
          color: 'var(--warn-ink)',
          backgroundColor: 'var(--warn-bg)',
          '& .MuiAlert-icon': { color: 'inherit' },
        },
        standardError: {
          color: 'var(--alert-danger-ink)',
          backgroundColor: 'var(--alert-danger-bg)',
          '& .MuiAlert-icon': { color: 'inherit' },
        },
        standardInfo: {
          color: 'var(--alert-info-ink)',
          backgroundColor: 'var(--alert-info-bg)',
          '& .MuiAlert-icon': { color: 'inherit' },
        },
      },
    },
    MuiCircularProgress: {
      defaultProps: { color: 'primary' },
    },
    // Tables, and the two toggle controls, each in a file of their own so the table look
    // and the switch and checkbox look each have one owner. Same tokens, same theme.
    ...tableOverrides,
    ...controlOverrides,
  },
});

/**
 * `CssBaseline` is MUI's reset. It stays, because it predates this work and the React
 * screens are laid out expecting it; Tailwind's own reset (preflight) is off for exactly
 * the opposite reason, which is that it would reach the Angular screens too.
 */
export default function Theme({ children }) {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* Radix tooltips need one provider above them. Mounted here so a screen never has
          to remember, and so the delay is configured in one place. */}
      <TooltipProvider delayDuration={200} skipDelayDuration={300}>
        {children}
      </TooltipProvider>
    </ThemeProvider>
  );
}

export { theme };

/** The dialog layer, exported so a portal outside MUI can match it. */
export const dialogZIndex = dialog.z;
